import {
  forwardRef,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Button,
  ButtonProps,
  ClickAwayListener,
  Popper,
  PopperPlacementType,
  TextField,
  Typography,
  Unstable_TrapFocus as TrapFocus,
} from '@material-ui/core';
import { MAX_REASON_LENGTH, validateRequiredReason } from './utils';
import * as Styles from './Orders.css';

interface RejectBubbleProps {
  id: string;
  busy: boolean;
  placement: PopperPlacementType;
  arrowRef: (element: HTMLSpanElement | null) => void;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

// The bubble's content mounts on every opening, so the reason starts empty
// and focus moves to it. The ref lets the focus trap hold the bubble.
const RejectBubble = forwardRef<HTMLDivElement, RejectBubbleProps>(
  ({ id, busy, placement, arrowRef, onCancel, onConfirm }, ref) => {
    const intl = useIntl();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    const confirm = () => {
      const validation = validateRequiredReason(reason);
      setError(validation);
      if (validation) {
        inputRef.current?.focus();
        return;
      }
      onConfirm(reason.trim());
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCancel();
      }
    };

    return (
      <Styles.RejectBubble
        ref={ref}
        id={id}
        role="dialog"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-consequence`}
        data-placement={placement}
        elevation={8}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <Typography id={`${id}-title`} variant="subtitle1" component="h2">
          {intl.formatMessage({ id: 'MY_ORDERS.REJECT_TITLE' })}
        </Typography>
        {/* Draft consequence text agreed at the 28.09 steering meeting; the
          final wording is still to be provided by the business. */}
        <Typography id={`${id}-consequence`} variant="body2">
          {intl.formatMessage({ id: 'MY_ORDERS.REJECT_CONSEQUENCE' })}
        </Typography>
        <Box mt={2}>
          <TextField
            id={`${id}-reason`}
            inputRef={inputRef}
            fullWidth
            multiline
            required
            minRows={3}
            variant="outlined"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setError('');
            }}
            label={intl.formatMessage({ id: 'MY_ORDERS.REJECTION_REASON' })}
            error={Boolean(error)}
            helperText={intl.formatMessage({
              id: error || 'MY_ORDERS.REJECT_REASON_HELP',
            })}
            inputProps={{ maxLength: MAX_REASON_LENGTH + 1 }}
          />
        </Box>
        <Box mt={2} display="flex" justifyContent="flex-end" gridGap={8}>
          <Button disabled={busy} onClick={onCancel}>
            {intl.formatMessage({ id: 'ORDERS.CANCEL' })}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            disabled={busy}
            onClick={confirm}
          >
            {intl.formatMessage({ id: 'MY_ORDERS.REJECT_SUBMIT' })}
          </Button>
        </Box>
        <Styles.BubbleArrow ref={arrowRef} />
      </Styles.RejectBubble>
    );
  },
);

interface RejectOrderButtonProps {
  orderNumber: string;
  busy?: boolean;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  onReject: (reason: string) => unknown;
}

// Reject button whose confirmation opens in a bubble above it (decision of
// 02.10) instead of a dialog: what rejecting means, the required reason and
// Cancel / Confirm. Escape and a click outside cancel; focus moves into the
// bubble and back to the button. Nothing is sent before the confirmation.
const RejectOrderButton = ({
  orderNumber,
  busy = false,
  size,
  variant,
  onReject,
}: RejectOrderButtonProps) => {
  const intl = useIntl();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const mounted = useRef(true);
  const restoreFocus = useRef(false);
  const [arrow, setArrow] = useState<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);
  const id = `reject-order-${orderNumber}`;
  // Stable modifiers so a re-render does not recreate the popper.
  const modifiers = useMemo(
    () => ({
      flip: { enabled: true },
      preventOverflow: { enabled: true, boundariesElement: 'viewport' },
      arrow: { enabled: Boolean(arrow), element: arrow },
    }),
    [arrow],
  );

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  // Runs after the bubble has unmounted, so focus lands on the button.
  useEffect(() => {
    if (!open && restoreFocus.current) {
      restoreFocus.current = false;
      anchorRef.current?.focus();
    }
  }, [open]);

  const close = () => {
    restoreFocus.current = true;
    setOpen(false);
  };

  const confirm = async (reason: string) => {
    await onReject(reason);
    if (mounted.current) {
      close();
    }
  };

  return (
    <>
      <Button
        ref={anchorRef}
        color="secondary"
        size={size}
        variant={variant}
        disabled={busy}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {intl.formatMessage({ id: 'MY_ORDERS.REJECT' })}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="top"
        style={{ zIndex: 1300 }}
        modifiers={modifiers}
      >
        {({ placement }) => (
          <ClickAwayListener
            onClickAway={(event) => {
              if (!anchorRef.current?.contains(event.target as Node)) {
                close();
              }
            }}
          >
            <div>
              <TrapFocus
                open
                disableAutoFocus
                disableRestoreFocus
                getDoc={() => document}
                isEnabled={() => true}
              >
                <RejectBubble
                  id={id}
                  busy={busy}
                  placement={placement}
                  arrowRef={setArrow}
                  onCancel={close}
                  onConfirm={confirm}
                />
              </TrapFocus>
            </div>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default RejectOrderButton;
